import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
    Alert, Box, Collapse, Grid, MenuItem, TextField,
    FormControl, FormHelperText, FormLabel, RadioGroup, FormControlLabel, Radio

} from "@mui/material";

import { AISLES_LAYER as CAPTURE_LAYER, ZONES_LAYER } from "../tools/useLayout";
import { DRAWING_WAYS, getTypeParamError, ORIENTATIONS } from "../tools/useLayoutAnnotations";
import { isValidJson, validateAlphanumeric } from "../tools/utils";
import CircularLoader from "../tools/CircularLoader";
import CircleColor from "./CircleColor";

const DEFAULT_VALUES = {
    aisle: '',
    clientName: '',
    orientation: '',
    part: '',
    params: '{}',
    linearType: 'aisle',
    zone: '',
}

export default forwardRef(function DialogCreationAisleSide({
    layers,
    colissionLayers,
    dialogData,
    isUniqueName,
    addNewLayer,
    onDisabledChange,
    linearTypes = [],
    linearTypeParams = {},
    isLoadingLinearTypes = false,
    getLinearTypes,
}, ref) {
    // Setup
    const { t } = useTranslation();

    // State
    const [formState, setFormState] = useState(DEFAULT_VALUES);
    const [errors, setErrors] = useState({});

    // Derived values
    const collisionZones = useMemo(() => {
        const { data: { start, end } } = dialogData;
        const startPoint = { x: start[0], y: start[1] };
        const endPoint = { x: end[0], y: end[1] };
        return colissionLayers(CAPTURE_LAYER, { start: startPoint, end: endPoint }, DRAWING_WAYS.LINE, 'partial');
    }, [dialogData, colissionLayers]);

    // Zones the aisle_side line overlaps, sorted by coverage (largest first).
    // Drives the zone selector: an aisle may straddle two zones, so each side is
    // resolved by its own line overlap rather than inheriting the parent aisle.
    const zoneCollisions = useMemo(() => {
        const { data: { start, end } } = dialogData;
        const startPoint = { x: start[0], y: start[1] };
        const endPoint = { x: end[0], y: end[1] };
        return colissionLayers(ZONES_LAYER, { start: startPoint, end: endPoint }, DRAWING_WAYS.LINE, 'partial');
    }, [dialogData, colissionLayers]);

    const availableOrientations = useMemo(() => {
        const subLayerKeys = Object.keys(layers?.[CAPTURE_LAYER]?.layers?.[formState?.aisle]?.subLayers || {});
        return ORIENTATIONS.filter(orientation => {
            return !subLayerKeys.find(layer => layer === `${formState?.aisle}-${orientation}`);
        });
    }, [layers, formState?.aisle]);

    const isNeccesaryPart = useMemo(() => {
        const subLayerKeys = Object.keys(layers?.[CAPTURE_LAYER]?.layers?.[formState?.aisle]?.subLayers || {});
        const regex = new RegExp(`^${formState?.aisle}-${formState?.orientation}-\\d+?$`);
        return subLayerKeys.filter(layer => regex.test(layer))?.length >= 1
    }, [layers, formState?.aisle, formState?.orientation]);

    const existsError = useMemo(() => Object.keys(errors).filter(key => !!errors[key]).length > 0, [errors]);

    const typeParamError = useMemo(
        () => getTypeParamError(formState.linearType, formState.params, linearTypeParams),
        [formState.linearType, formState.params, linearTypeParams]
    );

    const isSaveDisabled = !!typeParamError || !linearTypes.length;

    // Effects
    // Retries fetching the aisle types if a previous request failed
    useEffect(() => {
        getLinearTypes?.();
    }, [getLinearTypes]);

    useEffect(() => {
        if (collisionZones?.length === 1) {
            setFormState(prev => ({ ...prev, aisle: collisionZones[0].layer }));
        }
    }, [collisionZones]);

    // Default to zoneCollisions[0] (largest overlap) — must match the backend
    // criterion so the default equals what the robot gets. Guard keeps a manual
    // choice from being overwritten.
    useEffect(() => {
        if (zoneCollisions?.length > 0) {
            setFormState(prev => (prev.zone ? prev : { ...prev, zone: zoneCollisions[0].layer }));
        }
    }, [zoneCollisions]);

    useEffect(() => {
        // Trigger concatenation of orientation and part to validate if it is unique or required
        if ((formState?.aisle && collisionZones?.length > 1) || formState?.part || formState?.orientation) {
            validateAllFields();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formState?.part, formState?.orientation]);

    useEffect(() => {
        onDisabledChange?.(isSaveDisabled);
    }, [isSaveDisabled, onDisabledChange]);

    // Functions
    const validateField = useCallback((field, value) => {
        const rules = {
            aisle: { required: true },
            orientation: { required: true },
            part: { required: isNeccesaryPart, mustBeNumber: true, minValue: 1, isUnique: true },
            params: { mustBeJson: true },
            linearType: { required: true },
            clientName: { isAlphanumericWithSymbols: true },
        };
        const rule = rules?.[field];
        if (!rule) return null;
        if (rule.required && (!value || value === ''))
            return 'This field is required';
        if (rule.mustBeNumber && (value && (!/^\d+$/.test(value) || value < rule.minValue)))
            return 'This field must be a number greater than 0';
        if (rule.isUnique && rule.required && !isUniqueName(`${formState?.aisle}-${formState?.orientation}-${value}`, CAPTURE_LAYER, formState?.aisle))
            return 'This combination of aisle and orientation already exists';
        if (rule.mustBeJson && !isValidJson(value))
            return 'This field must be a valid JSON object';
        if (rule && rule.isAlphanumericWithSymbols && value) {// Alphanumeric with symbols logic
            if (!validateAlphanumeric(value, { allowSymbols: true })) {
                return t('overseer_app.layouts.helper.client_name_helper', 'Only letters, numbers, underscores (_), and hyphens (-) are allowed.');
            }
        }
        return null;
    }, [isNeccesaryPart, isUniqueName, formState?.aisle, formState?.orientation]);

    const validateAllFields = useCallback(() => {
        const errors = {};
        Object.keys(formState).forEach(field => {
            const error = validateField(field, formState[field]);
            if (error) errors[field] = error;
        });
        setErrors(errors);
        if (Object.keys(errors).length > 0) return false;
        return true;
    }, [formState, validateField]);

    const onSave = useCallback(() => {
        const isValid = validateAllFields();
        if (!isValid) return false;
        const { data: { start, end } } = dialogData;
        const { aisle } = formState;
        addNewLayer(CAPTURE_LAYER, {
            name: aisle,
            clientName: formState?.clientName,
            bbox: [{
                x: start[0],
                y: start[1]
            }, {
                x: end[0],
                y: end[1]
            }],
            type: DRAWING_WAYS.LINE,
            orientation: formState?.orientation,
            aisle_part: formState?.part,
            params: formState?.params ? JSON.stringify(JSON.parse(formState?.params)) : '{}',
            linearType: formState.linearType,
            zone: formState?.zone || null,
        });
        return true
    }, [validateAllFields, dialogData, formState, addNewLayer]);

    const handleFieldChange = useCallback((field, value) => {
        setErrors(prev => ({ ...prev, [field]: validateField(field, value) || undefined }));
        setFormState(prev => {
            const next = { ...prev, [field]: value };
            if (field === 'linearType') {
                try {
                    const parsed = JSON.parse(prev.params || '{}');
                    const prevConfig = linearTypeParams[prev.linearType];
                    if (prevConfig) delete parsed[prevConfig.key];
                    const nextConfig = linearTypeParams[value];
                    if (nextConfig) parsed[nextConfig.key] = nextConfig.value;
                    next.params = JSON.stringify(parsed, null, 2);
                } catch { /* invalid params JSON, skip injection */ }
            }
            return next;
        });
    }, [validateField, linearTypeParams]);

    useImperativeHandle(ref, () => ({
        callbackOnSave: onSave,
    }), [onSave]);

    return <>
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Collapse in={existsError}>
                    <Alert severity="error">
                        {t('overseer_app.layouts.creation.default_error', 'Some fields are required or must be unique to create a new annotation in this Layer.')}
                        {errors?.part && ' ' + t('overseer_app.layouts.creation.combination_error', 'Combination of orientation and part already exists or the part is not valid')}
                    </Alert>
                </Collapse>
            </Grid>
            <Grid item xs={3}>
                <TextField label={t('overseer_app.layouts.aisle', 'Aisle')} fullWidth required select value={formState?.aisle} onChange={(e) => setFormState(prev => ({ ...prev, aisle: e.target.value }))} disabled={collisionZones?.length <= 1} error={!!errors.aisle}>
                    {collisionZones?.map(({ layer }) => (
                        <MenuItem value={layer} key={layer}>{layer}</MenuItem>
                    ))}
                </TextField>
            </Grid>
            <Grid item xs={4}>
                <TextField required label={t('overseer_app.layouts.aisle_orientation', 'Orientation')} fullWidth value={formState?.orientation} onChange={(e) => handleFieldChange('orientation', e.target.value)} error={!!errors.orientation} select disabled={!formState?.aisle}>
                    {ORIENTATIONS.map(orientation => (
                        <MenuItem value={orientation} key={orientation} disabled={!availableOrientations?.includes(orientation)}>{orientation}</MenuItem>
                    ))}
                </TextField>
            </Grid>
            <Grid item xs={5}>
                <TextField label={t('overseer_app.layouts.aisle_part', 'Aisle Part')} fullWidth value={formState?.part} onChange={(e) => handleFieldChange('part', e.target.value)} error={!!errors.part} required={isNeccesaryPart} disabled={!formState?.aisle || !formState?.orientation} />
            </Grid>
            {zoneCollisions?.length > 0 && (
                <Grid item xs={12}>
                    {/* Hint only when there are several zones and the default
                        (largest overlap, [0]) is still selected. */}
                    {zoneCollisions.length > 1 && formState?.zone === zoneCollisions[0]?.layer && (
                        <Alert severity="info" sx={{ mb: 1 }}>
                            {t('overseer_app.layouts.creation.zone_default_helper', 'This is the zone with the largest overlap. You can pick another one.')}
                        </Alert>
                    )}
                    <TextField label={t('overseer_app.layouts.zone', 'Zone')} fullWidth select value={formState?.zone} onChange={(e) => handleFieldChange('zone', e.target.value)}>
                        {zoneCollisions.map(({ layer, color }) => (
                            <MenuItem value={layer} key={layer}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                    {layer}
                                    <CircleColor color={color} />
                                </Box>
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
            )}
            <Grid item xs={12}>
                <TextField label={t('overseer_app.layouts.creation.linear_client_name', 'Client\'s linear name')} fullWidth onChange={(e) => handleFieldChange('clientName', e.target.value)} value={formState?.clientName}
                    error={!!errors?.clientName}
                    helperText={
                        errors?.clientName ||
                        t('overseer_app.layouts.helper.client_name_helper', 'Only letters, numbers, underscores (_), and hyphens (-) are allowed.')}
                />
            </Grid>
            <Grid item xs={12}>
                <FormControl component="fieldset" required error={!!errors.linearType}>
                    <FormLabel component="legend">{t('overseer_app.layouts.aisle_type', 'Aisle type')} </FormLabel>
                    {isLoadingLinearTypes ? (
                        <CircularLoader size="2em" />
                    ) : !linearTypes.length ? (
                        <FormHelperText error>
                            {t('overseer_app.layouts.linear_types_error', 'Aisle types could not be loaded. Close and reopen this dialog to try again.')}
                        </FormHelperText>
                    ) : (
                        <RadioGroup
                            row
                            name="linearType"
                            value={formState.linearType}
                            onChange={(e) => handleFieldChange('linearType', e.target.value)}
                        >
                            {linearTypes.map((value) => (
                                <FormControlLabel key={value} value={value} control={<Radio />} label={t(`overseer_app.layouts.linear_types.${value}`, value)} />
                            ))}
                        </RadioGroup>
                    )}
                    {typeParamError && (
                        <FormHelperText sx={{ color: 'warning.main' }}>
                            {typeParamError.kind === 'missing_param'
                                ? t('overseer_app.layouts.linear_type_param_required',
                                    'Parameters must include "{{key}}: true" for the selected type.',
                                    { key: linearTypeParams[formState.linearType]?.key })
                                : t('overseer_app.layouts.linear_type_param_conflict',
                                    'Parameters contain "{{key}}: true" which belongs to type "{{type}}". Update the type selector or remove the parameter.',
                                    { key: linearTypeParams[typeParamError.foreignType]?.key, type: typeParamError.foreignType })
                            }
                        </FormHelperText>
                    )}
                </FormControl>
            </Grid>
            <Grid item xs={12}>
                <TextField multiline minRows={2} maxRows={10} label={t('overseer_app.layouts.parameters', 'Parameters')} fullWidth value={formState?.params} onChange={(e) => handleFieldChange('params', e.target.value)} error={!!errors.params} helperText={t('overseer_app.layouts.creation.parameters_helper', 'This field must be a valid JSON object')} />
            </Grid>
        </Grid>
    </>
})